import type { RootState } from 'src/redux/store';

import { toast } from 'sonner';
import { useForm } from 'react-hook-form';
import { useLocation } from 'react-router';
import { useState, useEffect } from 'react';
import { useBoolean } from 'minimal-shared/hooks';
import { zodResolver } from '@hookform/resolvers/zod';

import Grid from '@mui/material/Grid2';
import { Box, Button, Dialog, IconButton, DialogTitle, DialogContent } from '@mui/material';

import validationSchema from 'src/utils/validationSchema';

import { useTranslate } from 'src/locales';
import { useAppSelector } from 'src/hook/hooks';
import { AccountForm } from 'src/pages/accounts/components/account-form';
import { useGetAssigneeList } from 'src/pages/tickets/hooks/useGetAssigneeList';
import { useModulePermissions } from 'src/pages/auth/hooks/useModulePermissions';
import { defaultAccount, type IAccount } from 'src/pages/accounts/accounts.types';
import { SelectRecordDialog } from 'src/pages/chat/components/select-record-dialog';
import { CustomAutocomplete } from 'src/pages/tickets/components/custom-autocomplete';
import {
  useCreateAccountMutation,
  useGetAccountDtlMutation,
} from 'src/pages/accounts/slice/accountsApi';

import { Iconify } from 'src/components/iconify';
import { Form, Field } from 'src/components/hook-form';

import type { IContact } from '../contacts.types';

type Props = {
  open: boolean;
  onClose: () => void;
  selectedUser?: IContact;
  handleSubmitData: (data: IContact) => void;
  isCallPopup?: boolean;
};

export function ContactForm({
  open,
  onClose,
  selectedUser,
  handleSubmitData,
  isCallPopup = false,
}: Props) {
  const [getAccountDtl] = useGetAccountDtlMutation();
  const [createAccount] = useCreateAccountMutation();
  const { isView, isCreate } = useModulePermissions('company');
  const { loginApiData } = useAppSelector((state) => (state as RootState).auth);

  const location = useLocation();
  const noPermission = new Set(['account']);
  const hasPermission = location.pathname.split('/').some((item) => noPermission.has(item)); // `some` is more efficient

  const { t } = useTranslate('translation');
  const selectRecordDialog = useBoolean();
  const accountMenuDialog = useBoolean();

  const [type, setType] = useState<'Contact' | 'Account' | ''>('');
  const [selectedRecord, setSelectedRecord] = useState<any>(null);
  const [selectedAccount, setSelectedAccount] = useState<IAccount | undefined>(undefined);

  const [selectedUserAccDtl, setSelectedUserAccDtl] = useState<{
    label: string;
    value: string;
  } | null>(null);

  type CreateContactSchemaType = {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    address: string;
    city: string;
    nationalId: string;
    country: string;
    companyId: string;
    assignedId: string;
  };

  const fldList = [
    { name: 'firstName', required: false },
    { name: 'lastName', required: true },
    { name: 'email', required: false },
    { name: 'phone', required: false },
    { name: 'address', required: false },
    { name: 'city', required: false },
    { name: 'nationalId', required: false },
    { name: 'country', required: false },
    { name: 'companyId', required: false },
    { name: 'assignedId', required: true },
  ];

  const ContactSchema = validationSchema(fldList, t);
  // const defaultFilter = {
  //   // combineFieldsGroups: ['firstName', 'lastName'],
  //   filters: [],
  //   globalSearch: '',
  //   limit: 10,
  //   page: 1,
  // };
  const defaultValues: CreateContactSchemaType = {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    nationalId: '',
    country: '',
    companyId: '',
    assignedId: '',
  };
  function convertNullToString(obj: any) {
    return Object.fromEntries(
      Object.entries(obj).map(([key, value]) => [key, value === null ? '' : value])
    );
  }
  function convertStringToNull(obj: any) {
    return Object.fromEntries(
      Object.entries(obj).map(([key, value]) => [key, value === '' ? null : value])
    );
  }
  const methods = useForm<CreateContactSchemaType>({
    mode: 'all',
    resolver: zodResolver(ContactSchema),
    defaultValues,
    values: selectedUser
      ? {
        ...defaultValues,
        ...convertNullToString({ ...selectedUser, assignedId: '' }),
      } // Ensures `name` is never undefined
      : defaultValues,
  });

  const { reset, handleSubmit, setValue, watch } = methods;

  const onSubmit = handleSubmit(async (data: Partial<IContact>) => {
    const updatedPhoneFld = data.phone?.includes('+') ? data.phone?.replace(/\+/g, '') : data.phone;

    const updatedData = { ...data, phone: updatedPhoneFld };

    if (data.assignedId) {
      if (typeof data.assignedId === 'object' && 'id' in data.assignedId) {
        updatedData['assignedId'] = data.assignedId.id ?? '';
        updatedData['assignedType'] = data.assignedId.isUser ? 'USER' : 'TEAM';
      } else {
        updatedData['assignedId'] = data.assignedId;
      }
    } else {
      delete updatedData?.assignedId;
    }

    handleSubmitData({
      ...updatedData,
      companyId: selectedUserAccDtl?.value ?? '',
      id: selectedUser?.id ?? '',
      ...(selectedUser?.id
        ? loginApiData && 'id' in loginApiData
          ? { modifiedById: loginApiData.id }
          : {}
        : loginApiData && 'id' in loginApiData
          ? { createdById: loginApiData.id }
          : {}),
    } as IContact);
  });

  const onCloseHandler = () => {
    setSelectedUserAccDtl(null);
    onClose();
    reset();
  };

  const searchBtnHandler = (typ: '' | 'Contact' | 'Account') => {
    setType(typ);
    selectRecordDialog.onTrue();
  };

  const onDialogClose = () => {
    selectRecordDialog.onFalse();
  };

  const setRecordInputHandler = (selectedRow: any) => {
    setSelectedRecord(selectedRow);
    onDialogClose();
  };

  const handleCreateAccSubmit = async (account: Partial<IAccount>) => {
    delete account.id;
    await createAccount(convertStringToNull(account))
      .unwrap()
      .then((res) => {
        if (res) {
          toast.success(t('ACCOUNT_CREATED_SUCCESSFULLY'));
          // Add new created acc dtl in create form.
          setValue('companyId', res?.name || '');
          setSelectedUserAccDtl({ label: res?.name || '', value: res?.id || '' });
        }
      })
      .catch((err) => {
        toast.error(t('ACCOUNT_CREATION_FAILED'));
      });

    accountMenuDialog.onFalse();
    setSelectedAccount(undefined)
  };


  const handleCreateAccount = () => {
    setSelectedAccount({
      ...defaultAccount,
      assignedId: loginApiData && 'id' in loginApiData && loginApiData?.id,
      assignedType: 'USER',
    });
    accountMenuDialog.onTrue();
  }

  const handleCloseAccountForm = () => {
    accountMenuDialog.onFalse();
    setSelectedAccount(undefined);
  };

  const watchAssignedId = watch('assignedId');

  const {
    _recordList,
    searchValue,
    isFetching,
    open: openList,
    listScrollHandler,
    searchValueHandler,
    openListHandler,
    closeListHandler,
    selectedAssigneeHandler,
  } = useGetAssigneeList({ setValue: setValue as any, selectedTicket: selectedUser });

  useEffect(() => {
    selectedAssigneeHandler(watchAssignedId);
  }, [watchAssignedId]);

  useEffect(() => {
    if (selectedUser && selectedUser.companyId) {
      getAccountDtl(selectedUser?.companyId)
        .unwrap()
        .then((res) => {
          const companyDtl = res?.data?.company ?? null;
          setValue('companyId', companyDtl?.name);
          setSelectedUserAccDtl({ label: companyDtl?.name || '', value: companyDtl?.id || '' });
        })
        .catch((err) => {
          toast.error('Something Went Wrong');
        });
    }
  }, [selectedUser]);

  useEffect(() => {
    if (selectedRecord) {
      setSelectedUserAccDtl({ label: selectedRecord?.name || '', value: selectedRecord?.id || '' });
      setValue('companyId', selectedRecord?.name);
    }
  }, [selectedRecord]);

  const renderSelectRecordDialog = () => (
    <SelectRecordDialog
      open={selectRecordDialog.value}
      onClose={onDialogClose}
      type={type}
      setRecordInputHandler={setRecordInputHandler}
    />
  );

  const renderAccountForm = () => (
    <AccountForm
      open={accountMenuDialog.value}
      onClose={handleCloseAccountForm}
      handleSubmitData={handleCreateAccSubmit}
      selectedUser={selectedAccount}
    />
  );

  return (
    <>
      <Dialog
        fullWidth
        maxWidth="sm"
        open={open}
        onClose={onCloseHandler}
        sx={{
          '& .MuiPaper-rounded': {
            p: 0.5,
            boxShadow: '0px 0px 30px rgba(255, 255, 255, 0.1)',
          },
        }}
      >
        <Box>
          <DialogTitle sx={[(theme) => ({ p: theme.spacing(3, 3, 2, 3) })]}>
            {!isCallPopup && selectedUser?.firstName !== '' ? t('EDIT') : t('CREATE')}{' '}
            {t('CONTACT')}
          </DialogTitle>
          <DialogContent dividers sx={{ pt: 1, pb: 0, border: 'none' }}>
            <Form methods={methods} onSubmit={onSubmit}>
              <Grid container spacing={2} sx={{ my: 2 }}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Field.Text name="firstName" label={t('FIRST_NAME')} />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Field.Text name="lastName" label={t('LAST_NAME')} />
                </Grid>
              </Grid>
              <Grid container spacing={2} sx={{ my: 2 }}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Field.Text name="email" label={t('EMAIL')} type="Email" />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Field.Phone
                    name="phone"
                    label={t('CONTACT_NUM')}
                    placeholder={`${t('ENTER')} ${t('CONTACT_NUM')}`}
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2} sx={{ my: 2 }}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Field.Text name="address" label={t('ADDRESS')} />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Field.Text name="city" label={t('CITY')} />
                </Grid>
              </Grid>
              <Grid container spacing={2} sx={{ my: 2 }}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Field.Text name="nationalId" label={t('NATIONAL_ID')} />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Field.Text name="country" label={t('COUNTRY')} />
                </Grid>
              </Grid>

              <Box sx={{ my: 2 }}>
                <CustomAutocomplete
                  name="assignedId"
                  label={t('ASSIGNED_TO')}
                  isOptionEqualToValue={(a, b) => a.id === b.id}
                  placeholder="Select a User/Team"
                  options={_recordList}
                  searchValue={searchValue}
                  listScrollHandler={listScrollHandler}
                  searchValueHandler={searchValueHandler}
                  open={openList}
                  openListHandler={openListHandler}
                  closeListHandler={closeListHandler}
                  isFetching={isFetching}
                />
              </Box>
              <Box sx={{ my: 2 }}>
                <RenderSearchField
                  name="companyId"
                  label={t('ACCOUNT_NAME')}
                  onSearchBtnClick={() => searchBtnHandler('Account')}
                  onAddBtnClick={handleCreateAccount}
                  disabled={hasPermission || !isView}
                  isAccountCreate={!isCreate}
                  setSelectedUserAccDtl={setSelectedUserAccDtl}
                />
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'end', my: 2 }}>
                <Button variant="outlined" color="inherit" onClick={onCloseHandler}>
                  {t('CANCEL')}
                </Button>

                <Button type="submit" variant="contained" sx={{ ml: 2 }}>
                  {t('SAVE')}
                </Button>
              </Box>
            </Form>
          </DialogContent>
        </Box>
      </Dialog>
      {selectRecordDialog.value && renderSelectRecordDialog()}
      {renderAccountForm()}
    </>
  );
}

type RenderSearchFieldType = {
  name: string;
  label: string;
  onSearchBtnClick: () => void;
  onAddBtnClick: () => void;
  disabled?: boolean;
  setSelectedUserAccDtl?: (data: any) => void;
  isAccountCreate?: boolean;
};

const RenderSearchField = ({
  name,
  label,
  onSearchBtnClick,
  onAddBtnClick,
  disabled = false,
  setSelectedUserAccDtl = () => {},
  isAccountCreate = false,
}: RenderSearchFieldType) => (
  <Box sx={{ my: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2 }}>
    <Field.Text
      name={name}
      label={label}
      disabled
      slotProps={{ inputLabel: { shrink: true } }}
      clear={!disabled}
      setSelectedUserAccDtl={setSelectedUserAccDtl}
    />

    <IconButton
      aria-label="search"
      disabled={disabled}
      sx={{
        background: disabled ? '#85B0A0 !important' : '#50a28a',
        opacity: 1,
      }}
      onClick={onSearchBtnClick}
      disableRipple
    >
      <Iconify icon="eva:search-fill" sx={{ color: 'white' }} />
    </IconButton>

    <IconButton
      aria-label="search"
      disabled={disabled || isAccountCreate}
      sx={{
        background: disabled || isAccountCreate ? '#85B0A0 !important' : '#50a28a',
        opacity: 1,
      }}
      onClick={onAddBtnClick}
      disableRipple
    >
      <Iconify icon="mingcute:add-line" sx={{ color: 'white' }} />
    </IconButton>
  </Box>
);

// type CreateContactSchemaType = zod.infer<typeof ContactSchema>;

// const ContactSchema = zod.object({
//   firstName: zod.string().min(1, { message: `${t('FIRST_NAME')} ${t('IS_REQUIRED')}` }),
//   lastName: zod.string().min(1, { message: `${t('LAST_NAME')} ${t('IS_REQUIRED')}` }),
//   email: zod
//     .string()
//     .min(1, { message: `${t('EMAIL')} ${t('IS_REQUIRED')}` })
//     .email({ message: t('VALID_EMAIL') }),
//   phone: zod.string().min(1, { message: `${t('PHONE_NUM')} ${t('IS_REQUIRED')}` }),
//   address: zod.string().min(1, { message: `${t('ADDRESS')} ${t('IS_REQUIRED')}` }),
//   city: zod.string().min(1, { message: `${t('CITY')} ${t('IS_REQUIRED')}` }),
//   pincode: zod.string().min(1, { message: `${t('PINCODE')} ${t('IS_REQUIRED')}` }),
//   country: zod.string().min(1, { message: `${t('COUNTRY')} ${t('IS_REQUIRED')}` }),
// });
